import { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../../Contexts/TenantContext";
import { useAuth } from "../../Contexts/AuthContext";
import { useScrollLock } from "../../Hooks/useScrollLock";
import { formatPhoneNumber, matchListCase } from "../../Utils/DataFormatters";
import { FaTimes, FaChevronDown } from "react-icons/fa";

const toTitleCase = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const rowStateClass = (isCurrent: boolean) =>
  isCurrent ? "bg-primary-120 text-primary-600 font-medium" : "hover:bg-gray-50";

interface NavLink {
  path: string;
  label: string;
}

interface Category {
  id: string;
  name: string;
}

// Structured item model used by the classic layout so it can render an
// expandable PRODUTOS entry with category sub-items. The alt layout keeps
// using the flat navLinks + filteredCategories rendering untouched.
export type SidebarItem =
  | { kind: "link"; path: string; label: string }
  | { kind: "products"; label: string; basePath: string; allLabel: string };

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  navLinks: NavLink[];
  filteredCategories: Category[];
  handleLogout: () => void;
  /** When provided, renders the structured classic menu (with PRODUTOS submenu)
   *  instead of the flat alt menu. */
  items?: SidebarItem[];
  /** Breakpoint at which the sidebar is hidden (desktop nav takes over).
   *  The classic layout uses "lg". The alt layout uses "none" because it decides
   *  when to swap to the mobile nav by measuring the desktop row, not by a
   *  CSS breakpoint. */
  breakpoint?: "md" | "lg" | "none";
}

const MobileSidebar = ({
  isOpen,
  onClose,
  navLinks,
  filteredCategories,
  handleLogout,
  items,
  breakpoint = "md",
}: MobileSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant, tenantContact } = useTenant();
  const { user } = useAuth();

  const isContactRestricted = user?.requiresContactInfoUpdate;
  const isFullyAuthenticated = !!user && !isContactRestricted;

  const hiddenClass = breakpoint === "lg" ? "lg:hidden" : breakpoint === "none" ? "" : "md:hidden";

  const [productsOpen, setProductsOpen] = useState(false);

  const productsItem = items?.find((item) => item.kind === "products");
  const productsPath = productsItem?.kind === "products" ? productsItem.basePath : "/produtos";
  const isOnProductList = location.pathname === productsPath;
  const currentCategoryId = isOnProductList ? new URLSearchParams(location.search).get("categoria") : null;

  const isCurrentPath = (path: string) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  // The product list shows its current entry inside the submenu, so the submenu starts open there
  useEffect(() => {
    if (isOpen) {
      setProductsOpen(isOnProductList);
    }
  }, [isOpen, isOnProductList]);

  const handleNav = (path: string) => {
    onClose();
    setTimeout(() => navigate(path), 300);
  };

  const handleLogoutWithClose = () => {
    onClose();
    setTimeout(() => handleLogout(), 300);
  };

  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);

  useScrollLock(isOpen);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (isOpen) {
      setShouldRender(true);
      setIsVisible(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { if (!cancelled) setIsVisible(true); });
      });
    } else {
      setIsVisible(false);
      timer = setTimeout(() => setShouldRender(false), 300);
    }
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [isOpen]);

  if (!shouldRender) {
    return null;
  }

  const renderProductsItem = (item: Extract<SidebarItem, { kind: "products" }>) => (
    <Fragment key="products">
      <li>
        <button
          onClick={() => setProductsOpen((prev) => !prev)}
          className={`w-full text-left px-4 py-4 flex justify-between items-center ${rowStateClass(isCurrentPath(item.basePath) && !(isOnProductList && productsOpen))}`}
          aria-expanded={productsOpen}
        >
          {item.label}
          <FaChevronDown className={`text-xs transition-transform ${productsOpen ? "rotate-180" : ""}`} />
        </button>
      </li>
      {productsOpen && (
        <>
          <li>
            <button
              onClick={() => handleNav(item.basePath)}
              className={`w-full text-left block py-4 pl-8 pr-4 font-medium ${rowStateClass(isOnProductList && !currentCategoryId)}`}
            >
              {matchListCase(item.allLabel, filteredCategories.map((cat) => cat.name))}
            </button>
          </li>
          {filteredCategories.map((cat) => (
            <li key={cat.id}>
              <button
                onClick={() => handleNav(`${item.basePath}?categoria=${cat.id}`)}
                className={`w-full text-left block py-4 pl-8 pr-4 ${rowStateClass(currentCategoryId === cat.id)}`}
              >
                {cat.name}
              </button>
            </li>
          ))}
        </>
      )}
    </Fragment>
  );

  return (
    <div className={`${hiddenClass} fixed inset-0 z-50 flex ${!isVisible ? "pointer-events-none" : ""}`}>
      <div className={`w-4/5 max-w-xs bg-white h-full overflow-y-auto shadow-xl flex flex-col transform transition-transform duration-300 ease-out ${isVisible ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between px-4 py-4 bg-black">
          {isFullyAuthenticated ? (
            <p className="text-sm font-bold text-white">Olá, {user.firstName ?? "Cliente"}!</p>
          ) : (
            <div className="text-sm text-white">
              <p className="font-semibold mb-0.5">Olá, Visitante!</p>
              <button onClick={() => handleNav("/login")} className="underline font-bold transition-opacity hover:opacity-75">Entre</button>
              {" "}ou{" "}
              <button onClick={() => handleNav("/cadastro")} className="underline font-bold transition-opacity hover:opacity-75">cadastre-se</button>
              {" "}aqui
            </div>
          )}
          <button onClick={onClose} className="text-white transition-opacity hover:opacity-75">
            <FaTimes />
          </button>
        </div>

        <ul className="flex flex-col divide-y border-b -mb-px text-sm text-gray-700">
          {items ? (
            items.map((item) =>
              item.kind === "products"
                ? renderProductsItem(item)
                : (
                  <li key={item.path}>
                    <button onClick={() => handleNav(item.path)} className={`w-full text-left block px-4 py-4 ${rowStateClass(isCurrentPath(item.path))}`}>
                      {item.label}
                    </button>
                  </li>
                )
            )
          ) : (
            <>
              {navLinks.map((link) => (
                <li key={link.path}>
                  <button onClick={() => handleNav(link.path)} className={`w-full text-left block px-4 py-4 ${rowStateClass(isCurrentPath(link.path))}`}>
                    {link.label}
                  </button>
                </li>
              ))}
              {filteredCategories.map((cat) => (
                <li key={cat.id}>
                  <button
                    onClick={() => handleNav(`/produtos?categoria=${cat.id}`)}
                    className={`w-full text-left px-4 py-4 flex justify-between items-center ${rowStateClass(currentCategoryId === cat.id)}`}
                  >
                    {toTitleCase(cat.name)}
                  </button>
                </li>
              ))}
            </>
          )}
          {isFullyAuthenticated && (
            <>
              <li><button onClick={() => handleNav("/meus-dados")} className={`w-full text-left block px-4 py-4 ${rowStateClass(isCurrentPath("/meus-dados"))}`}>Meus dados</button></li>
              <li><button onClick={() => handleNav("/pedidos")} className={`w-full text-left block px-4 py-4 ${rowStateClass(isCurrentPath("/pedidos"))}`}>Meus pedidos</button></li>
              <li><button onClick={handleLogoutWithClose} className="w-full text-left px-4 py-4 hover:bg-gray-50 text-gray-600">Sair</button></li>
            </>
          )}
        </ul>

        {tenantContact?.whatsAppNumber && (
          <div className="mt-auto border-t divide-y text-xs text-gray-500">
            <p className="px-4 py-3"><span className="font-semibold">WhatsApp:</span> {formatPhoneNumber(tenantContact.whatsAppNumber)}</p>
            {tenant?.workingHours && <p className="px-4 py-3"><span className="font-semibold">Horários:</span> {tenant.workingHours}</p>}
          </div>
        )}
      </div>
      <div className={`flex-1 bg-black/40 transition-opacity duration-300 ${isVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} onClick={onClose} />
    </div>
  );
};

export default MobileSidebar;
